import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, Package, ChevronLeft, ChevronRight, Plus, Trash2, 
  Edit2, X, Check, Phone, User, MessageSquare, RefreshCw,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOCKER_SIZES = {
  small: { label: 'S - Small', color: 'bg-blue-500' },
  medium: { label: 'M - Medium', color: 'bg-green-500' },
  xlarge: { label: 'XL - Extra Large', color: 'bg-purple-500' }
};

const BLOCK_TYPES = [
  { value: 'whatsapp', label: 'Prenotazione WhatsApp' },
  { value: 'phone', label: 'Prenotazione Telefonica' },
  { value: 'maintenance', label: 'Manutenzione' },
  { value: 'manual', label: 'Altro' }
];

const MONTH_NAMES = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const AdminAvailabilityPage = () => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [dayAvailability, setDayAvailability] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [error, setError] = useState(null);
  
  // Block form state
  const [blockForm, setBlockForm] = useState({
    locker_size: 'medium',
    quantity: 1,
    date: '',
    start_time: '06:00',
    end_time: '22:00',
    reason: '',
    customer_name: '',
    customer_phone: '',
    notes: '',
    block_type: 'whatsapp'
  });

  useEffect(() => {
    fetchCalendarData();
  }, [currentMonth]);

  useEffect(() => {
    if (selectedDate) {
      fetchDayAvailability(selectedDate);
    }
  }, [selectedDate]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      const response = await axios.get(
        `${API_URL}/api/admin/locker-availability-calendar?start_date=${formatDate(startDate)}&end_date=${formatDate(endDate)}`,
        { withCredentials: true }
      );
      setCalendarData(response.data.calendar || []);
    } catch (err) {
      console.error('Error fetching calendar:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/ub-gestione-2026');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDayAvailability = async (date) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/locker-availability/${date}`,
        { withCredentials: true }
      );
      setDayAvailability(response.data);
      
      // Also fetch blocks for this date
      const blocksResponse = await axios.get(
        `${API_URL}/api/admin/locker-blocks?date=${date}`,
        { withCredentials: true }
      );
      setBlocks(blocksResponse.data.blocks || []);
    } catch (err) {
      console.error('Error fetching day availability:', err);
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleCreateBlock = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/locker-blocks`,
        blockForm,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setShowBlockModal(false);
        resetBlockForm();
        if (selectedDate) {
          fetchDayAvailability(selectedDate);
        }
        fetchCalendarData();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la creazione del blocco');
    }
  };

  const handleUpdateBlock = async () => {
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/locker-blocks/${editingBlock.id}`,
        blockForm,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setShowBlockModal(false);
        setEditingBlock(null);
        resetBlockForm();
        if (selectedDate) {
          fetchDayAvailability(selectedDate);
        }
        fetchCalendarData();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante l\'aggiornamento');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo blocco?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/locker-blocks/${blockId}`,
        { withCredentials: true }
      );
      
      if (selectedDate) {
        fetchDayAvailability(selectedDate);
      }
      fetchCalendarData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const openEditModal = (block) => {
    setEditingBlock(block);
    setBlockForm({
      locker_size: block.locker_size,
      quantity: block.quantity,
      date: block.date,
      start_time: block.start_time,
      end_time: block.end_time,
      reason: block.reason || '',
      customer_name: block.customer_name || '',
      customer_phone: block.customer_phone || '',
      notes: block.notes || '',
      block_type: block.block_type || 'manual'
    });
    setShowBlockModal(true);
  };

  const openCreateModal = (date = null) => {
    resetBlockForm();
    if (date) {
      setBlockForm(prev => ({ ...prev, date }));
    }
    setShowBlockModal(true);
  };

  const resetBlockForm = () => {
    setBlockForm({
      locker_size: 'medium',
      quantity: 1,
      date: selectedDate || '',
      start_time: '06:00',
      end_time: '22:00',
      reason: '',
      customer_name: '',
      customer_phone: '',
      notes: '',
      block_type: 'whatsapp'
    });
    setEditingBlock(null);
    setError(null);
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = [];
    const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDate(date);
      const dayData = calendarData.find(d => d.date === dateStr);
      
      days.push({
        day,
        date,
        dateStr,
        isPast: date < today,
        isToday: date.toDateString() === today.toDateString(),
        isSelected: selectedDate === dateStr,
        availability: dayData?.availability || null
      });
    }

    return days;
  };

  const getAvailabilityColor = (dayObj) => {
    if (!dayObj?.availability) return '';
    
    const { small, medium, xlarge } = dayObj.availability;
    const totalAvailable = (small?.available || 0) + (medium?.available || 0) + (xlarge?.available || 0);
    const totalCapacity = (small?.total || 5) + (medium?.total || 12) + (xlarge?.total || 8);
    
    const percentage = (totalAvailable / totalCapacity) * 100;
    
    if (percentage === 100) return 'bg-green-900/30';
    if (percentage >= 50) return 'bg-yellow-900/30';
    if (percentage > 0) return 'bg-orange-900/30';
    return 'bg-red-900/30';
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/ub-gestione-2026/prenotazioni')}
            className="p-2 hover:bg-[#1A1A1A] rounded transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="font-heading text-3xl font-bold">Gestione Disponibilità</h1>
            <p className="text-gray-400">Calendario e blocchi manuali locker</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <div className="bg-[#141414] border border-[#2A2A2A] p-6">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  className="p-2 hover:bg-[#2A2A2A] rounded transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-semibold">
                  {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h2>
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  className="p-2 hover:bg-[#2A2A2A] rounded transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Day Names */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
                  <div key={day} className="text-center text-gray-500 text-sm py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {generateCalendarDays().map((dayObj, index) => (
                  <button
                    key={dayObj ? dayObj.dateStr : `empty-${index}`}
                    onClick={() => dayObj && !dayObj.isPast && setSelectedDate(dayObj.dateStr)}
                    disabled={!dayObj || dayObj.isPast}
                    className={`
                      aspect-square p-1 flex flex-col items-center justify-center text-sm transition-all rounded
                      ${!dayObj ? 'invisible' : ''}
                      ${dayObj?.isPast ? 'text-gray-600 cursor-not-allowed' : 'hover:border-[#FFCC00] border border-transparent'}
                      ${dayObj?.isToday ? 'border-[#FFCC00] border' : ''}
                      ${dayObj?.isSelected ? 'bg-[#FFCC00] text-[#0A0A0A] font-bold' : ''}
                      ${!dayObj?.isSelected && !dayObj?.isPast ? getAvailabilityColor(dayObj) : ''}
                    `}
                  >
                    <span>{dayObj?.day}</span>
                    {dayObj?.availability && !dayObj.isPast && (
                      <div className="flex gap-0.5 mt-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${dayObj.availability.small?.available > 0 ? 'bg-blue-500' : 'bg-gray-600'}`} />
                        <span className={`w-1.5 h-1.5 rounded-full ${dayObj.availability.medium?.available > 0 ? 'bg-green-500' : 'bg-gray-600'}`} />
                        <span className={`w-1.5 h-1.5 rounded-full ${dayObj.availability.xlarge?.available > 0 ? 'bg-purple-500' : 'bg-gray-600'}`} />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Legend */}
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className="text-gray-400">Small</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-gray-400">Medium</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-purple-500" />
                  <span className="text-gray-400">XL</span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <span className="w-4 h-4 bg-green-900/30 rounded" />
                  <span className="text-gray-400">100%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 bg-yellow-900/30 rounded" />
                  <span className="text-gray-400">50%+</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 bg-red-900/30 rounded" />
                  <span className="text-gray-400">Pieno</span>
                </div>
              </div>
            </div>
          </div>

          {/* Day Details Panel */}
          <div className="lg:col-span-1">
            {selectedDate ? (
              <div className="bg-[#141414] border border-[#2A2A2A] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">{selectedDate}</h3>
                  <button
                    onClick={() => openCreateModal(selectedDate)}
                    className="flex items-center gap-2 bg-[#FFCC00] hover:bg-[#E6B800] text-[#0A0A0A] px-3 py-1.5 text-sm font-bold transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Blocca
                  </button>
                </div>

                {dayAvailability && (
                  <div className="space-y-4">
                    {/* Availability Summary */}
                    <div>
                      <h4 className="text-sm text-gray-400 mb-2">Disponibilità</h4>
                      {Object.entries(dayAvailability.availability || {}).map(([size, data]) => (
                        <div key={size} className="flex items-center justify-between py-2 border-b border-[#2A2A2A]">
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${LOCKER_SIZES[size]?.color || 'bg-gray-500'}`} />
                            <span>{LOCKER_SIZES[size]?.label || size}</span>
                          </div>
                          <div className="text-right">
                            <span className={`font-bold ${data.available > 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {data.available}
                            </span>
                            <span className="text-gray-500">/{data.total}</span>
                            {data.blocked_manual > 0 && (
                              <span className="text-orange-400 text-xs ml-2">({data.blocked_manual} bloccati)</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Manual Blocks */}
                    {blocks.length > 0 && (
                      <div>
                        <h4 className="text-sm text-gray-400 mb-2">Blocchi Manuali</h4>
                        <div className="space-y-2">
                          {blocks.map(block => (
                            <div key={block.id} className="bg-[#1A1A1A] p-3 rounded border border-[#2A2A2A]">
                              <div className="flex items-center justify-between mb-2">
                                <span className={`px-2 py-0.5 text-xs rounded ${LOCKER_SIZES[block.locker_size]?.color || 'bg-gray-500'}`}>
                                  {block.quantity}× {LOCKER_SIZES[block.locker_size]?.label || block.locker_size}
                                </span>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => openEditModal(block)}
                                    className="p-1 hover:bg-[#2A2A2A] rounded"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteBlock(block.id)}
                                    className="p-1 hover:bg-red-900/50 rounded text-red-400"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              <div className="text-xs text-gray-400">
                                <div>{block.start_time} - {block.end_time}</div>
                                {block.customer_name && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <User className="w-3 h-3" />
                                    {block.customer_name}
                                  </div>
                                )}
                                {block.customer_phone && (
                                  <div className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {block.customer_phone}
                                  </div>
                                )}
                                {block.reason && (
                                  <div className="flex items-center gap-1 mt-1 text-[#FFCC00]">
                                    <MessageSquare className="w-3 h-3" />
                                    {block.reason}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Online Bookings Preview */}
                    {dayAvailability.availability && Object.values(dayAvailability.availability).some(d => d.bookings?.length > 0) && (
                      <div>
                        <h4 className="text-sm text-gray-400 mb-2">Prenotazioni Online</h4>
                        <div className="space-y-2">
                          {Object.entries(dayAvailability.availability).map(([size, data]) =>
                            data.bookings?.map(booking => (
                              <div key={booking.booking_code} className="bg-[#0A0A0A] p-2 rounded text-xs">
                                <div className="flex justify-between">
                                  <span className="font-mono text-[#FFCC00]">{booking.booking_code}</span>
                                  <span>{booking.start_time} - {booking.end_time}</span>
                                </div>
                                <div className="text-gray-400">{booking.name}</div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-[#141414] border border-[#2A2A2A] p-6 text-center">
                <Calendar className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Seleziona una data dal calendario per vedere i dettagli</p>
              </div>
            )}
          </div>
        </div>

        {/* Block Modal */}
        {showBlockModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#141414] border border-[#2A2A2A] p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold">
                  {editingBlock ? 'Modifica Blocco' : 'Nuovo Blocco Manuale'}
                </h3>
                <button onClick={() => { setShowBlockModal(false); resetBlockForm(); }} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {error && (
                <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-2 mb-4 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {/* Block Type */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Tipo</label>
                  <select
                    value={blockForm.block_type}
                    onChange={(e) => setBlockForm({ ...blockForm, block_type: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] p-2 text-white"
                  >
                    {BLOCK_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Data *</label>
                  <input
                    type="date"
                    value={blockForm.date}
                    onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] p-2 text-white"
                    required
                  />
                </div>

                {/* Locker Size & Quantity */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Dimensione *</label>
                    <select
                      value={blockForm.locker_size}
                      onChange={(e) => setBlockForm({ ...blockForm, locker_size: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] p-2 text-white"
                    >
                      <option value="small">S - Small</option>
                      <option value="medium">M - Medium</option>
                      <option value="xlarge">XL - Extra Large</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Quantità *</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={blockForm.quantity}
                      onChange={(e) => setBlockForm({ ...blockForm, quantity: parseInt(e.target.value) || 1 })}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] p-2 text-white"
                    />
                  </div>
                </div>

                {/* Time Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Dalle</label>
                    <input
                      type="time"
                      value={blockForm.start_time}
                      onChange={(e) => setBlockForm({ ...blockForm, start_time: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] p-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Alle</label>
                    <input
                      type="time"
                      value={blockForm.end_time}
                      onChange={(e) => setBlockForm({ ...blockForm, end_time: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] p-2 text-white"
                    />
                  </div>
                </div>

                {/* Customer Info (for WhatsApp/Phone bookings) */}
                {(blockForm.block_type === 'whatsapp' || blockForm.block_type === 'phone') && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Nome Cliente</label>
                      <input
                        type="text"
                        value={blockForm.customer_name}
                        onChange={(e) => setBlockForm({ ...blockForm, customer_name: e.target.value })}
                        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] p-2 text-white"
                        placeholder="Mario Rossi"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Telefono Cliente</label>
                      <input
                        type="tel"
                        value={blockForm.customer_phone}
                        onChange={(e) => setBlockForm({ ...blockForm, customer_phone: e.target.value })}
                        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] p-2 text-white"
                        placeholder="+39 333 1234567"
                      />
                    </div>
                  </>
                )}

                {/* Reason */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Motivo / Note</label>
                  <textarea
                    value={blockForm.reason}
                    onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#2A2A2A] p-2 text-white h-20"
                    placeholder="Es: Prenotazione via WhatsApp, pagamento in contanti"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => { setShowBlockModal(false); resetBlockForm(); }}
                    className="flex-1 bg-[#2A2A2A] hover:bg-[#3A3A3A] text-white py-2 font-bold transition-colors"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={editingBlock ? handleUpdateBlock : handleCreateBlock}
                    className="flex-1 bg-[#FFCC00] hover:bg-[#E6B800] text-[#0A0A0A] py-2 font-bold transition-colors"
                  >
                    {editingBlock ? 'Salva' : 'Crea Blocco'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAvailabilityPage;
